import { useEffect, useMemo, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { IonPage, IonContent } from "@ionic/react";
import { Check, Flame, Repeat, Sparkles, SprayCan } from "lucide-react";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { ProgressBar } from "../components/ProgressBar";
import { PointsBadge } from "../components/PointsBadge";
import { CompletionCelebration, AllDoneCelebration } from "../components/CompletionCelebration";
import { PageHeader } from "../components/ui/PageHeader";
import { Avatar } from "../components/ui/Avatar";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { RoomIcon } from "../components/ui/RoomIcon";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { getStreakMessage } from "../logic/display-helpers";
import { cn } from "../lib/utils";
import type { ITask, IArea, ITaskCompletion } from "../types";

// ---------- Shared bits ----------

const CARD_BASE =
  "relative bg-card rounded-3xl p-4 border border-border shadow-soft transition-all duration-500";

const DoneFlash = ({ label }: { label: string }) => (
  <div className="flex items-center justify-center py-3 gap-2" role="status">
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white">
      <Check className="w-5 h-5" strokeWidth={3} aria-hidden="true" />
    </span>
    <span className="text-emerald-700 dark:text-emerald-300 font-bold">{label}</span>
  </div>
);

const FrequencyLabel = ({ task }: { task: ITask }) => (
  <span className="inline-flex items-center gap-1 text-muted-foreground text-[13px]">
    <Repeat className="w-3.5 h-3.5" aria-hidden="true" />
    Every {task.frequency_number} {task.frequency_unit}
  </span>
);

// ---------- Allocated task card (used when allocations exist) ----------

const AllocatedTaskCard = ({
  allocation,
  task,
  area,
  onMarkDone,
  justCompleted,
}: {
  allocation: ITaskCompletion;
  task: ITask | undefined;
  area: IArea | undefined;
  onMarkDone: (completionId: string) => void;
  justCompleted?: boolean;
}) => {
  const isDone = !!allocation.completed_at;
  const taskName = task?.name ?? "Unknown Task";
  const areaName = area?.name ?? "";

  return (
    <div
      className={cn(
        CARD_BASE,
        justCompleted
          ? "opacity-0 scale-95 -translate-x-4"
          : isDone
            ? "opacity-60 shadow-none bg-surface"
            : "opacity-100"
      )}
    >
      {justCompleted ? (
        <DoneFlash label="Done!" />
      ) : (
        <>
          <div className="flex items-start gap-3">
            <RoomIcon name={areaName} size="sm" />
            <div className="flex-1 min-w-0">
              <h3
                data-testid="task-name"
                className={cn(
                  "font-bold leading-snug",
                  isDone ? "line-through text-muted-foreground" : "text-foreground"
                )}
              >
                {taskName}
              </h3>
              {areaName && (
                <div className="text-muted-foreground text-[13px]">{areaName}</div>
              )}
            </div>
            {task && <PointsBadge points={task.points} />}
          </div>

          {!isDone && (
            <div className="flex items-center justify-between gap-3 mt-3">
              {task ? <FrequencyLabel task={task} /> : <span />}
              <Button
                onClick={() => onMarkDone(allocation.uuid)}
                aria-label={`Mark Done: ${taskName}`}
                leadingIcon={<Check className="w-4 h-4" aria-hidden="true" />}
              >
                Mark Done
              </Button>
            </div>
          )}

          {isDone && (
            <div className="mt-2">
              <Badge tone="success" icon={<Check className="w-3 h-3" aria-hidden="true" />}>
                Completed
              </Badge>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Fallback: cleanliness-based card ----------

const OutstandingTaskCard = ({
  task,
  onComplete,
  area,
  dirtyPercentage,
  justCompleted,
}: {
  task: ITask;
  onComplete: (taskId: string) => void;
  area?: IArea;
  dirtyPercentage: number;
  justCompleted?: boolean;
}) => {
  const areaName = area?.name || "Unknown Area";

  return (
    <div
      className={cn(
        CARD_BASE,
        justCompleted ? "opacity-0 scale-95 -translate-x-4" : "opacity-100"
      )}
    >
      {justCompleted ? (
        <DoneFlash label={`Done! +${task.points} pts`} />
      ) : (
        <>
          <div className="flex items-start gap-3 mb-3">
            <RoomIcon name={area?.name} size="sm" />
            <div className="flex-1 min-w-0">
              <h3 data-testid="task-name" className="text-foreground font-bold leading-snug">
                {task.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <span className="text-muted-foreground text-[13px]">{areaName}</span>
                {task.is_high_priority && <Badge tone="purple">Priority</Badge>}
              </div>
            </div>
            <PointsBadge points={task.points} />
          </div>

          <div className="mb-3">
            <ProgressBar percentage={dirtyPercentage} />
          </div>

          <div className="flex items-center justify-between gap-3">
            <FrequencyLabel task={task} />
            <Button
              onClick={() => onComplete(task.uuid)}
              aria-label={`Mark Done: ${task.name}`}
              leadingIcon={<Check className="w-4 h-4" aria-hidden="true" />}
            >
              Mark Done
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Streak display ----------

const StreakDisplay = ({ streak }: { streak: number }) => {
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 mb-6 bg-gradient-to-br from-sky-500 to-violet-600 shadow-glow-cyan dark:from-sky-700 dark:to-violet-800">
      <div
        aria-hidden="true"
        className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10"
      />
      <div
        aria-hidden="true"
        className="absolute right-10 -bottom-10 w-24 h-24 rounded-full bg-white/10"
      />
      <div className="relative flex items-center gap-4">
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 text-white"
        >
          <Flame className="w-8 h-8" />
        </span>
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <motion.span
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              className="text-4xl font-extrabold tracking-tight text-white leading-none"
            >
              {streak}
            </motion.span>
            <span className="text-white font-bold">Day Streak</span>
          </div>
          <p className="text-white/90 text-sm font-medium mt-1">{getStreakMessage(streak)}</p>
        </div>
      </div>
    </div>
  );
};

// ---------- Loading skeleton ----------

const TodaySkeleton = () => (
  <IonPage>
    <IonContent>
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4" role="status" aria-label="Loading tasks">
        <Skeleton className="h-9 w-40 rounded-full" />
        <Skeleton className="h-24 rounded-3xl" />
        <Skeleton className="h-36 rounded-3xl" />
        <Skeleton className="h-36 rounded-3xl" />
      </div>
    </IonContent>
  </IonPage>
);

// ---------- Main page ----------

export default function TodayPage() {
  const history = useHistory();
  const { user } = useAuth();

  // Zustand store
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const userProfile = useStore((s) => s.userProfile);
  const tasks = useStore((s) => s.tasks);
  const areas = useStore((s) => s.areas);
  const loading = useStore((s) => s.loading);
  const completeTask = useStore((s) => s.completeTask);
  const setCurrentHousehold = useStore((s) => s.setCurrentHousehold);
  const cleanlinessMap = useStore((s) => s.cleanlinessMap);
  const todayAllocations = useStore((s) => s.todayAllocations);
  const markAllocationDone = useStore((s) => s.markAllocationDone);

  const currentUserHousehold = userHouseholds.find(
    (uh) => uh.household_uuid === currentHouseholdId
  );

  const currentStreak = currentUserHousehold?.current_streak ?? 0;

  // Track recently completed items for animation
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [celebratingId, setCelebratingId] = useState<string | null>(null);
  const [celebratingPoints, setCelebratingPoints] = useState(0);
  const [showAllDone, setShowAllDone] = useState(false);

  const animateCompletion = useCallback((id: string, pts = 0) => {
    setCelebratingId(id);
    setCelebratingPoints(pts);
    setCompletedIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setCompletedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setCelebratingId(null);
    }, 800);
  }, []);

  // Set initial household
  useEffect(() => {
    if (!currentHouseholdId && userHouseholds.length > 0) {
      setCurrentHousehold(userHouseholds[0].household_uuid);
    }
  }, [userHouseholds, currentHouseholdId, setCurrentHousehold]);

  // Whether we have server-side allocations for today
  const hasAllocations = todayAllocations.length > 0;

  // Split allocations into to-do and done
  const todoAllocations = useMemo(
    () => todayAllocations.filter((a) => !a.completed_at),
    [todayAllocations]
  );
  const doneAllocations = useMemo(
    () => todayAllocations.filter((a) => !!a.completed_at),
    [todayAllocations]
  );

  // Today's earned points
  const todayPoints = useMemo(
    () => doneAllocations.reduce((sum, a) => sum + a.points_earned, 0),
    [doneAllocations]
  );

  // Fallback: outstanding tasks from cleanliness data
  const outstandingTasks = useMemo(() => {
    if (hasAllocations) return [];
    return tasks.filter((task) => {
      const dirty = cleanlinessMap[task.uuid] ?? 0;
      return dirty >= 60 || task.is_high_priority;
    });
  }, [tasks, cleanlinessMap, hasAllocations]);

  const sortedFallbackTasks = useMemo(() => {
    return [...outstandingTasks].sort((a, b) => {
      if (a.is_high_priority && !b.is_high_priority) return -1;
      if (!a.is_high_priority && b.is_high_priority) return 1;
      return (cleanlinessMap[b.uuid] ?? 0) - (cleanlinessMap[a.uuid] ?? 0);
    });
  }, [outstandingTasks, cleanlinessMap]);

  const handleCompleteTask = async (taskId: string) => {
    if (!user?.id) return;
    const task = tasks.find((t) => t.uuid === taskId);
    animateCompletion(taskId, task?.points ?? 0);
    try {
      await completeTask(taskId);
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleMarkAllocationDone = async (completionId: string) => {
    const alloc = todayAllocations.find((a) => a.uuid === completionId);
    const task = alloc ? tasks.find((t) => t.uuid === alloc.task_uuid) : undefined;
    animateCompletion(completionId, task?.points ?? alloc?.points_earned ?? 0);
    try {
      await markAllocationDone(completionId);
      // Check if this was the last to-do allocation
      const remainingTodo = todoAllocations.filter((a) => a.uuid !== completionId);
      if (remainingTodo.length === 0) {
        setTimeout(() => setShowAllDone(true), 800);
      }
    } catch (error) {
      console.error("Error marking allocation done:", error);
    }
  };

  // Resolve task for an allocation
  const getTaskForAllocation = (a: ITaskCompletion) =>
    tasks.find((t) => t.uuid === a.task_uuid);

  const getAreaForTask = (task: ITask | undefined) =>
    task ? areas.find((ar) => ar.uuid === task.area_uuid) : undefined;

  if (loading.households || loading.tasks) {
    return <TodaySkeleton />;
  }

  if (!currentUserHousehold) {
    return null;
  }

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const displayName = userProfile?.name || user?.email || "You";

  return (
    <IonPage>
      <PageHeader
        title="Today"
        subtitle={todayLabel}
        actions={
          <button
            type="button"
            onClick={() => history.push("/settings")}
            aria-label={`${userProfile?.name?.split(" ")[0] || "Your"} profile and settings`}
            className="flex items-center gap-2 rounded-full pl-3 pr-1 py-1 min-h-[44px] hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="text-foreground font-semibold text-sm hidden min-[360px]:inline">
              {userProfile?.name?.split(" ")[0] || "You"}
            </span>
            <Avatar
              name={displayName}
              seed={user?.id}
              photoUrl={userProfile?.profile_photo_url}
              size="md"
            />
          </button>
        }
      />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          {/* Streak Display */}
          <StreakDisplay streak={currentStreak} />

          {/* Progress summary */}
          {hasAllocations && (
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                { label: "To Do", value: todoAllocations.length, color: "text-primary" },
                { label: "Done", value: doneAllocations.length, color: "text-emerald-600 dark:text-emerald-400" },
                { label: "Points", value: todayPoints, color: "text-amber-600 dark:text-amber-400" },
              ].map((stat) => (
                <div key={stat.label} className="bg-surface rounded-2xl py-3 text-center">
                  <div className={cn("text-2xl font-extrabold tabular-nums", stat.color)}>
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground text-xs font-semibold">{stat.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* === Allocation-based view === */}
          {hasAllocations ? (
            <>
              {/* To Do */}
              {todoAllocations.length > 0 ? (
                <section className="mb-6">
                  <h2 className="section-title mb-3">To Do</h2>
                  <div className="space-y-3">
                    {todoAllocations.map((alloc) => {
                      const task = getTaskForAllocation(alloc);
                      return (
                        <AllocatedTaskCard
                          key={alloc.uuid}
                          allocation={alloc}
                          task={task}
                          area={getAreaForTask(task)}
                          onMarkDone={handleMarkAllocationDone}
                          justCompleted={completedIds.has(alloc.uuid)}
                        />
                      );
                    })}
                  </div>
                </section>
              ) : (
                <div className="text-center py-8 mb-6">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300 mb-3"
                  >
                    <Sparkles className="w-8 h-8" />
                  </span>
                  <h2 className="text-foreground text-xl font-bold mb-1">
                    All done for today!
                  </h2>
                  <p className="text-muted-foreground">
                    You've completed all your tasks. Great work!
                  </p>
                </div>
              )}

              {/* Done */}
              {doneAllocations.length > 0 && (
                <section className="mb-6">
                  <h2 className="section-title text-muted-foreground mb-3">Completed</h2>
                  <div className="space-y-2">
                    {doneAllocations.map((alloc) => {
                      const task = getTaskForAllocation(alloc);
                      return (
                        <AllocatedTaskCard
                          key={alloc.uuid}
                          allocation={alloc}
                          task={task}
                          area={getAreaForTask(task)}
                          onMarkDone={handleMarkAllocationDone}
                        />
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          ) : (
            <>
              {/* === Fallback: cleanliness-based view === */}
              {/* Task Status Banner */}
              <div className="flex items-center gap-3 bg-surface rounded-3xl p-4 mb-6">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-accent text-accent-foreground shrink-0"
                >
                  <SprayCan className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-foreground font-bold">
                    {outstandingTasks.length === 0
                      ? "All caught up!"
                      : "Ready to tackle some tasks?"}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {outstandingTasks.length === 0
                      ? "No outstanding tasks right now. Great job!"
                      : `${outstandingTasks.length} task${
                          outstandingTasks.length > 1 ? "s" : ""
                        } need${
                          outstandingTasks.length === 1 ? "s" : ""
                        } your attention`}
                  </p>
                </div>
              </div>

              {outstandingTasks.length > 0 ? (
                <section className="mb-6">
                  <h2 className="section-title mb-3">Outstanding Tasks</h2>
                  <div className="space-y-3">
                    {sortedFallbackTasks.map((task) => (
                      <OutstandingTaskCard
                        key={task.uuid}
                        task={task}
                        onComplete={handleCompleteTask}
                        area={areas.find((a) => a.uuid === task.area_uuid)}
                        dirtyPercentage={cleanlinessMap[task.uuid] ?? 0}
                        justCompleted={completedIds.has(task.uuid)}
                      />
                    ))}
                  </div>
                </section>
              ) : (
                <EmptyState
                  icon={Sparkles}
                  title="All done!"
                  description="No outstanding tasks right now. Check back later or add new tasks."
                  action={{
                    label: "Add New Area",
                    onClick: () => history.push("/areas/create"),
                  }}
                />
              )}
            </>
          )}
        </div>
      </IonContent>

      {/* Celebration overlays */}
      {celebratingId && (
        <CompletionCelebration
          points={celebratingPoints}
          show={!!celebratingId}
        />
      )}
      <AllDoneCelebration show={showAllDone} streakCount={currentStreak + 1} />
    </IonPage>
  );
}
